/**
 * Shared wire-clustering utilities used by both DocumentSearch (legacy diff)
 * and the multi-page search store (project view).
 */

export type ClusterableWire = {
  id: string;
  p1?: {x: number; y: number};
  p2?: {x: number; y: number};
};

export type NetWire = ClusterableWire & {
  net_id?: string;
};

/**
 * Groups wires by their net_id, filtering out wires without a net_id
 * and wires whose net_id doesn't appear in the provided nets map.
 */
export function groupWiresByNet<W extends NetWire>(
  wires: Record<string, W>,
  nets: Record<string, unknown>,
): Map<string, W[]> {
  const wiresByNet = new Map<string, W[]>();
  for (const wire of Object.values(wires)) {
    if (!wire.net_id) continue;
    if (!nets[wire.net_id]) continue;
    const bucket = wiresByNet.get(wire.net_id);
    if (bucket) bucket.push(wire);
    else wiresByNet.set(wire.net_id, [wire]);
  }
  return wiresByNet;
}

/**
 * Groups wires into connected components based on shared endpoints.
 *
 * Uses a Union-Find (disjoint-set) data structure with path-halving for
 * near-constant-time find operations. Two wires are connected if they
 * share an endpoint coordinate (matching x,y). Transitively connected
 * wires end up in the same cluster.
 *
 * @returns An array of wire-ID arrays, one per connected component.
 */
export function clusterWires(wires: ClusterableWire[]): string[][] {
  const parent = new Map<number, number>();
  function find(i: number): number {
    while (parent.get(i) !== i) {
      const p = parent.get(i)!;
      // Path-halving: point to grandparent to flatten the tree.
      parent.set(i, parent.get(p)!);
      i = p;
    }
    return i;
  }
  function union(a: number, b: number) {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  }

  for (let i = 0; i < wires.length; i++) parent.set(i, i);

  // Map "x,y" keys to wire indices that share that endpoint.
  const pointMap = new Map<string, number[]>();
  for (let i = 0; i < wires.length; i++) {
    const w = wires[i];
    if (!w.p1 || !w.p2) continue;
    for (const p of [w.p1, w.p2]) {
      const key = `${p.x},${p.y}`;
      const bucket = pointMap.get(key);
      if (bucket) {
        // All wires already in the bucket share this point, so they are
        // already unioned together. We only need to union with the first.
        union(i, bucket[0]);
        bucket.push(i);
      } else {
        pointMap.set(key, [i]);
      }
    }
  }

  // Collect groups by root representative.
  const groups = new Map<number, number[]>();
  for (let i = 0; i < wires.length; i++) {
    const root = find(i);
    const g = groups.get(root);
    if (g) g.push(i);
    else groups.set(root, [i]);
  }

  return Array.from(groups.values(), (indices) => indices.map((i) => wires[i].id));
}
